/**
 * Lazy Background Image Loader
 * Loads background images only when they enter the viewport
 */
(() => {
  const lazyBackgrounds = document.querySelectorAll(".lazy-bg-container");

  if (lazyBackgrounds.length === 0) return;

  function loadBackgroundImage(element) {
    if (!element?.dataset.src) {
      return;
    }
    // Create an image to preload
    const img = new Image();
    img.onload = () => {
      // Apply background only after image has loaded
      element.style.backgroundImage = `url('${element.dataset.src}')`;
      element.classList.add("lazy-bg-loaded");
    };
    img.onerror = () => {
      // Handle loading errors
      console.warn("Failed to load background image:", element.dataset.src);
      // Optionally set a fallback background or remove loading class
      element.classList.add("lazy-bg-error");
    };
    img.src = element.dataset.src;
  }

  if ("IntersectionObserver" in window) {
    const lazyBackgroundObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            return;
          }
          const lazyBackground = entry.target;
          loadBackgroundImage(lazyBackground);
          observer.unobserve(lazyBackground);
        });
      },
      {
        rootMargin: "200px 0px", // Start loading when element is 200px from viewport
        threshold: 0.01, // Trigger when at least 1% of the element is visible
      }
    );

    lazyBackgrounds.forEach(function (lazyBackground) {
      lazyBackgroundObserver.observe(lazyBackground);
    });
  } else {
    // Fallback for browsers without IntersectionObserver support
    lazyBackgrounds.forEach(loadBackgroundImage);
  }

  // Safety net - load any unloaded backgrounds after 5 seconds
  setTimeout(() => {
    lazyBackgrounds.forEach((lazyBackground) => {
      if (!lazyBackground.classList.contains("lazy-bg-loaded")) {
        loadBackgroundImage(lazyBackground);
      }
    });
  }, 5000);

  // Additional safety check for page changes (like language switches)
  document.addEventListener("DOMContentLoaded", () => {
    // Force check visibility after page is fully loaded
    lazyBackgrounds.forEach((lazyBackground) => {
      if (
        !lazyBackground.classList.contains("lazy-bg-loaded") &&
        isElementInViewport(lazyBackground)
      ) {
        loadBackgroundImage(lazyBackground);
      }
    });
  });

  // Helper function to check if element is in viewport
  function isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    return (
      rect.top <=
        (window.innerHeight || document.documentElement.clientHeight) + 200 &&
      rect.left <=
        (window.innerWidth || document.documentElement.clientWidth) &&
      rect.bottom >= -200 &&
      rect.right >= 0
    );
  }

  // Handle page visibility changes (tab switching, etc.)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      lazyBackgrounds.forEach((lazyBackground) => {
        if (
          !lazyBackground.classList.contains("lazy-bg-loaded") &&
          isElementInViewport(lazyBackground)
        ) {
          loadBackgroundImage(lazyBackground);
        }
      });
    }
  });
})();
